import { LayoutBlock } from '@FLAVOR/../../common/types';
import { css, CSSResultGroup, html } from 'lit';
import { customElement, property, query, queryAll, state } from 'lit/decorators';
import { DeviceAwareComponent } from './device-aware-component';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import { QuestionsService } from '@APP/services/questions-service';
import { DepInjMixin, requires } from '@APP/mixins/dependency';
import { Events } from '@APP/events/app-events';
import { eventBus } from '../../utils/event-bus';
import { arrowWhy, arrowWhyRight } from '../icons/icons';
import { classMap } from 'lit/directives/class-map';
import { t } from '@APP/utils/locale';
import { questionsDescription } from './lh-question/question.description';
import { LHImageUploadButton } from './lh-image-upload-button';

type Deps = {
  questionsService: QuestionsService;
};

@requires(QuestionsService.serviceName)
@customElement('lh-question-group')
export class LHQuestionGroup extends DepInjMixin<Deps>(DeviceAwareComponent) {

  @property({ type: Array })
    layout: LayoutBlock = [];

  @state()
    isValid = false;

  @state()
    hidden = false;

  @property({ type: Boolean })
    isActive = false;

  @property({ type: Boolean })
    noTilte = false;

  @property({ type: String })
    section = '';

  @queryAll('lh-question, lh-question-group')
  private questionElements!: any[];

  @query('lh-question[question-id="billing"]')
  private billingElement!: any;

  private subscriptions: Function[] = [];

  static get baseStyles() {
    return [
      css`
      ${questionsDescription},
      *:focus-visible {
        outline: 0px dashed transparent;
      }

      .questions-wrapper {
        display: flex;
        align-items: flex-start;
        flex-direction: row;
        justify-content: var(--question-wrapper-justify-content, unset);
        padding: var(--questions-wrapper-padding);
      }

      .questions-wrapper.questions-wrapper--card {
        max-width: 110rem;
        background: #FFFFFF;
        border: 1px solid #E3E0F3;
        box-shadow: 0px 3px 6px rgba(25, 72, 146, 0.06);
        border-radius: 1.5rem;
        padding: 3.3rem 5.6rem;
        margin: 1.5rem;
      }

      .padding-left {
        padding-left: 1.5rem;
        margin-top: -2rem;
      }

      .arrow-img {
        transform: translateY(0.25rem);
        place-self: baseline;
      }

      .arrow-img svg {
        max-height: 9rem;
      }

      .hidden, #question-description.hidden {
        display: none;
      }

      .group-subtitle {
        color: #707070;
        font-size: 2.5rem;
        font-weight: 500;
        margin-block-end: 5rem;
      }
      
      .group-subtitle.ocr {
        font-weight: 400;
        text-align: center;
        margin-block: 0;
        font-size: 2rem !important;
      }

    `,
    ];
  };

  static get mobileStyles() {
    return [
      css`
      #question-description {
        place-self: baseline;
      }

      .question-description--title {
        place-self: end start;
      }

      .arrow-img {
        transform: rotate(35deg) translateX(0.75rem) translateY(2rem);
        place-self: center;
      }

      .arrow-div {
        display: grid;
        width: 100%;
        grid-template-columns: 3fr 1fr;
      }

      .arrow-text {
        place-self: flex-start;
      }

      .questions-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 3rem 1rem;
        align-items: start;
      }

      .questions-wrapper.questions-wrapper--card .questions-grid {
        padding: 2.25rem;
      }

      .questions-wrapper.questions-wrapper--hasSideBar {
        flex-direction: column;
      }

      [data-layout] {
        grid-row-start: unset !important;
      }

      [data-layout="span_1"] {
        grid-column: span 1;
        margin-top: var(--question_span1_margin-top);
      }

      [data-layout="span_3"],
      [data-layout="span_2"],
      [data-layout="span_full"],
      [data-layout="span_4"] {
        grid-column: 1 / -1;
        grid-column-start: 1;
      }

      #questions-wrapper {
        max-width: 100%;
      }

      h2.group-title {
        margin: 2rem -2rem 2rem -2rem;
        padding: 1rem 2rem;
        font-size: 3rem;
      display: var(--group-title-display, block);
    }

    .questions-wrapper {
      box-sizing: border-box;
    }

   
      :host-context([current-section='S_Welcome']) .group-title {
        top: -0.2rem;
        padding: 0;
        margin: 2rem 0;
      }
      :host-context([current-section='S_Additional_Boaters']) .group-title {
        top: 0;
      }
      :host-context([current-section='S_Additional_Boaters']) .questions-wrapper {
        top: 0;
        padding: 0;
        margin: 0;
      }
      :host-context([current-section='S_Address']) .group-title {
        top: 0;
        padding: 0;
        margin: 0;
      }
      :host-context([current-section='S_Boat']) .group-title {
        top: -0.2rem;
        padding: 0;
        margin: 0;
      }
    `,
    ];
  };

  static get desktopStyles(): CSSResultGroup {
    return [
      css`
        .arrow-div.inline-start-margin {
          margin-inline-start: 3rem;
        }

      .arrow-div.block-start-margin {
        margin-block-start: 3rem;
      }

      .arrow-div {
        transform: translateY(3.5rem);
      }

      .arrow-div.position-absolute {
        position: absolute;
        right: 5rem;
        transform: translateY(0);
      }
      
      .grid-div{
        display:grid;
        grid-template-columns: auto auto;
        place-items: center;
      }

      .sidebar {
        padding-left: 7.5rem;
        border-left: 1px solid rgba(18, 18, 19, 0.1);
      }
 
      :host-context([current-section="S_Billing"]) .sidebar { padding-left: 2.5rem; }

      .questions-grid {
        display: var(--questions-grid-display, grid);
        width: var(--questions-grid-width, 100%);
        padding: 0 0 2rem;
        grid-template-columns: var(--grid-spacing, repeat(6, 15rem));
        grid-gap: 6rem 2rem;
        align-items: var(--grid-align, start);
      }
      .questions-grid.ocr {
        padding: 0;        
      }

      :host-context(.layout-content) .questions-wrapper--hasSideBar .questions-grid {
        --grid-spacing: repeat(6, minmax(0, 1fr));
        overflow: hidden auto;
      }

      .lh-question {
        text-align: var(--text-align);
      }

      [data-layout="span_2"] {
        grid-column: span 2;
      }

      [data-layout="span_3"] {
        grid-column: span 3;
      }

      [data-layout="span_4"] {
        grid-column: span 4;
      }

      [data-layout="span_full"] {
        grid-column: 1 / -1;
      }

      [data-layout="span_1"] {
        grid-column: span 1;
      }

      .group-title {
        font-size: 4rem;
        font-weight: 700;
      }

      .questions-wrapper.questions-wrapper--hasSideBar {
        max-width: 125rem;
        --questions-wrapper-width: auto;
        --grid-spacing: repeat(6, minmax(0, 1fr));
        --question-wrapper-justify-content: space-between;
      }

      :host-context([current-section="S_Billing"]) .questions-wrapper.questions-wrapper--hasSideBar,
      :host-context([current-section="S_Payment"]) .questions-wrapper.questions-wrapper--hasSideBar {
        align-items: normal;
      }

      :host-context([current-section="S_MTA_Coverages"]) .group-title {
        margin-inline: auto;
        max-width: 125rem;
      }

      :host-context([current-section="S_MTA_Coverages"]) .questions-wrapper.questions-wrapper--hasSideBar {
        margin-inline: auto;
      }
      :host-context([current-section="S_Welcome"]) .questions-wrapper.questions-wrapper--hasSideBar {
        max-width: 165rem;
      }

      .questions-wrapper.questions-wrapper--hasSideBar .questions-grid {
        width: 100%;
      }

      :host-context(.layout-content) {
        background-image: url('./assets/layout-content.svg');
        background-repeat: no-repeat;
        background-size: 100% 100%;
        filter: drop-shadow(0px 17px 41px #1948921a);
        height: 100vh;
        max-height: calc(100vh - 25rem);
      }

      :host-context(.layout-content) .group-title {
        padding: unset;
        margin: 0 0 10rem 10rem;
        top: 0;
        position: static;
      }

      :host-context(.layout-content) .questions-wrapper {
        margin-left: 3rem;
        margin-right: 3rem;
        padding-left: 8rem;
        padding-right: 8rem;
        overflow-y: auto;
        height: calc(100% - 20rem);
        width: auto;
      }

      :host-context(.layout-content) .questions-grid {
        grid-template-columns: var(--grid-spacing, repeat(6, 15rem));
        align-items: start;
        align-content: start;
      }

      :host-context(.layout-popup) {
        --questions-wrapper-width: 100%;
      }

      :host-context(.layout-popup) .questions-grid {
        gap: 2.5%;
        grid-template-columns: var(--grid-spacing, repeat(4, 1fr));
      }

      :host-context(.layout-popup) .group-title {
        padding: 2rem 0 2rem 0;
        margin: 0rem 2rem 3rem 0;
      }

      *::-webkit-scrollbar {
        width: 1rem;
        height: 1rem;
      }
      *::-webkit-scrollbar-track {
        border-radius: 16px;
        background: transparent;
      }
      *::-webkit-scrollbar-thumb {
        border-radius: 16px;
        background: rgba(193, 193, 193, 0.52);
      }

      .questions-grid {
        height: unset;
        flex-direction: row;
      }

      .questions-grid-horizontal {
        height: 72vh;
        font-size: 0.9em;
        display: grid;
        grid-auto-rows: 1fr;
        row-gap: 3rem;
        align-items: center;
        padding: 0 30px 0 0;
      }

      .questions-grid-top {
        height: 12vh;
      }

      .questions-wrapper {
        font-size: 1em;
        width: var(--questions-wrapper-width, max-content);
        padding: 0 2rem 0 0;
      }

      :host-context([current-section="S_Boat"]) {
        position: relative;
      }

     `,
    ];
  };
  static get rtlStyles() {
    return [
      css`
      :host{
        text-align: right;
      }
      .group-title {
      padding: 2rem 0 2rem 0;
      margin: 0rem 2rem 3rem 0;
      position: sticky;
      z-index: 1;
      color: var(--c-font-title);
      text-align: right;
      background: linear-gradient(180deg, #F6F6F9 56.71%, rgba(246, 246, 249, 0) 100%);
    }
    `];
  };

  static get ltrStyles() {
    return [
      css`
    :host{
      text-align: left;
    }
    h2:empty {
    min-height: 0px;
    display: contents;
    }
    .group-title {
      padding: calc(7.25rem + 2rem) 0 2rem 2rem;
      margin: calc(-1 * 7.25rem) 2rem 3rem -2rem;
      position: sticky;
      z-index: 1;
      color: var(--c-font-title);
      text-align: left;
      /*background: linear-gradient(180deg, #F6F6F9 56.71%, rgba(246, 246, 249, 0) 100%);*/
      background: var(--group-title-bg, var(--c-bg));
    }

    .group-title.subtitle {
      padding: unset;
      margin: unset;
    }

    .group-title.subtitle.ocr {
      text-align: center;
      padding: 0;
      margin: 0;
      font-size: 3rem;
    }
    
    .group-title.subtitle h2 {
      margin-bottom: 0;
    }
    `];
  };


  connectedCallback() {
    super.connectedCallback();
    this.subscriptions.push(
      eventBus().on(Events.Type.QuestionViewConditionChange, () => {
        this.requestUpdate();
      }),
      eventBus().on(Events.Type.ImagePreviewModeChange, () => {
        this.requestUpdate();
      }),
      eventBus().on(Events.Type.UnlockScreen, () => {
        this.billingElement?.scrollIntoView({ behavior: 'smooth' });
      }),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.subscriptions.forEach((f) => f());
  }


  isHidden(qId: string) {
    const question = this.deps.questionsService.model.getQuestionById(qId);
    if (question?.hide === true) return true;
    return false;
  }

  renderSideBar(sideBar: string) {
    return sideBar ? html`${unsafeHTML(`<${sideBar} class="sidebar"></${sideBar}>`)}` : html``;
  }

  renderArrow(qId: string, arrowStyleData: Record<string, string>) {
    const { arrowLocalStyle } = arrowStyleData;
    const question = this.deps.questionsService.model.getQuestionById(qId);
    const position = question?.descriptionPosition === 'right' ? 'right' : 'left';
    const mobile = this.userDevice === 'mobile';
    const img = position === 'right' ? arrowWhyRight : arrowWhy;
    const { descriptionClasses } = question?.metadata || {};
    let arrowClasses = 'arrow-div grid-div';
    if (descriptionClasses) {
      arrowClasses = `arrow-div grid-div ${descriptionClasses}`;
    }
    const imgClasses = {
      'arrow-img': true,
      'hidden': !!question?.hide,
    };
    const divClasses = {
      'padding-left': !mobile,
      'question-description': true,
      'hidden': !!question?.hide,
    };

    if (question?.description && question?.descriptionPosition === 'right' && !mobile) {
      const imgHtml = html`<div class=${classMap(imgClasses)}>${img}</div>`;
      const isDisplayPreviewImage = question?.description === 'D_Display_Preview_Image';
      const isMobile = this.userDevice === 'mobile';
      const isOcr = !isMobile && this.deps.questionsService.isOcr;
      const userSelectedOcr = this.deps.questionsService.model.getQuestionById('User_Selected_Ocr')?.value === '$_BOOL_true';
      // if the user has passed the S_Boat_OCR section but no ocr the data is used, this means that the user hasn't uploaded any image so we don't want to show the preview image
      const showImageUploadBtn = isOcr && userSelectedOcr;
      //desktop view
      const textHtml = html`
        <div id="question-description" class=${classMap(divClasses)}>
          <div class="question-description--title">${t(question.descriptionTitle || '')}</div>
          ${t(question.description)}
        </div>`;
      
      const regularArrowHtml = html`
        <div class="${arrowClasses}" style="${arrowLocalStyle}" data-layout="${question.metadata?.spanWidth || 'span_2'}">
        ${imgHtml}${textHtml}
        </div>
      `;

      const previewImageHtml = html`
      <div class="arrow-div grid-div position-absolute" style="${arrowLocalStyle}" data-layout="${question.metadata?.spanWidth === 'span_3' ? 'span_3' : 'span_2'}">
        <div class="license-image-preview">
          ${showImageUploadBtn?LHImageUploadButton.getInstance():''}
        </div>
        </div>
      `;
      return isDisplayPreviewImage ? previewImageHtml: regularArrowHtml;
    }
    return '';
  }

  private renderTitleAndSubtitle(layout: any) {
    const isMobile = this.userDevice === 'mobile';
    const isOcr = !isMobile && ['S_Boat_OCR_Details','S_Boat_OCR_Error'].includes(this.section);
    const isImagePreviewMode = this.deps.questionsService.imagePreviewMode;
    /** Title html */
    const titleHTML = html`<h2 class="group-title">${t(layout.title)}</h2>`;
    const titleHTMLwithSubtitle = html`<h2 class="group-title subtitle ${isOcr? 'ocr':''}">${isOcr && isImagePreviewMode ?t('Preview photo') :t(layout.title)}</h2>`;
    let response = titleHTML;

    /** Adding subtitle html if exists */
    const showSubtitle = layout.subtitle;
    let subtitleHTML;
    /** Updating response */
    if (showSubtitle) {
      subtitleHTML = html`<h2 class="group-subtitle ${isOcr? 'ocr':''}"> ${t(layout.subtitle)} </h2>`;
      response = html`
        <div class='title-and-subtitle-container ${isOcr? 'ocr':''}'>${titleHTMLwithSubtitle}${subtitleHTML}</div>`;
    }

    return response;
  }

  renderContent() {
    let lineIndex = 1;
    const showTitle = !this.noTilte;
    const isMobile = this.userDevice === 'mobile';
    const isOcr = !isMobile && this.deps.questionsService.isOcr;
    return this.layout.map((layout) => {
      let gridClass = layout.grid ? 'questions-grid' + '-' + layout.grid : 'questions-grid';
      if (isOcr) gridClass += ' ocr';
      const wrapperClass = {
        'questions-wrapper': true,
        'questions-wrapper--hasSideBar': layout.sideBar || '',
        'questions-wrapper--card': this.classList.contains('card'),
      };

      return html`
        ${showTitle ? this.renderTitleAndSubtitle(layout) : ''}
        <div class="${classMap(wrapperClass)}">
          <div class=${gridClass}>
            ${layout.questions.map((lineup) => {
    let emptyLine = true;
    const line = lineup.map((question) => {
      const [questionId, spanCount, sameLine] = question.split('::');

      let localStyle = `grid-row-start: ${sameLine === 'true' ? lineIndex - 1 : lineIndex}`;

      if (this.isHidden(questionId)) localStyle = 'display:none';
      else emptyLine = sameLine ? true : false;

      const arrowLocalStyle = this.userDevice === 'desktop' ? localStyle : `grid-row-start: ${lineIndex + 1}`;
      const arrowStyleData = { arrowLocalStyle, spanCount };
      return html`
              <lh-question style="${localStyle}" question-id=${questionId} tabindex="0" ?active=${this.isActive} @validchanged=${this.notifyValidChanged} data-layout="${spanCount}"></lh-question>
              ${this.renderArrow(questionId, arrowStyleData)}
              `;

    });
    if (!emptyLine) lineIndex++;
    return line;
  })}
          </div>
          ${this.renderSideBar(layout.sideBar || '')}      
        </div>  
      `;
    });
  }

  notifyValidChanged(e: CustomEvent) {
    this.isValid = Array.from(this.questionElements).every((el) => el.isValid);
    this.dispatchEvent(new CustomEvent(e.type, { bubbles: e.bubbles }));
  }

  public runValidationsNow() {
    Array.from(this.questionElements).forEach((el) => el.runValidationsNow());
  }

  public updateShowErrors() {
    const questions = Array.from(this.questionElements);
    if (questions && questions.length) {
      let hasFirstError: boolean = false;
      questions.forEach((el) => {
        el.updateShowErrors();
        if (!hasFirstError && el.showErrors && !el.hidden) {
          hasFirstError = true;
          el.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }

  public checkValidations() {
    Array.from(this.questionElements).forEach((el) => el.checkValidations());
    this.isValid = Array.from(this.questionElements).every((el) => el.isValid || el.hidden);
  }

  public getErrors(): string {
    return Array.from(this.questionElements).map((el) => el.getErrors()).filter(i => i != '').join(', ');
  }


}